import { Badge, Button, Group, Paper, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { MatchLineButton } from './MatchLineButton';
import { PLATFORM_ORDERS_PATH } from './OrdersArriving';

const LINES_PATH = '/admin/v1/channels/platform-order-lines';
const REMATCH_PATH = '/admin/v1/channels/platform-order-lines/rematch';
const MAPPINGS_PATH = '/admin/v1/channels/item-mappings';

interface PlatformLine {
  id?: string;
  channelId?: string;
  orderId?: string;
  platformName?: string;
  platformSku?: string | null;
  qty?: number;
  lineTotalMinor?: number;
  currency?: string;
  status?: string;
  /** The state of the order the line is on — see `nothingToRecover` below. */
  orderStatus?: string | null;
}

/**
 * An order the guest took back is not work, however unmatched its lines are.
 *
 * Nothing was sold and the platform owes nothing, so counting one here would put money into a total
 * headed "sold but not on a check" that nobody was ever charged, and send the owner off to match a
 * name to recover it. The server leaves these out of the rematch for the same reason; the two have
 * to agree or the button says it did nothing over a figure that says there is work left.
 */
const nothingToRecover = (line: PlatformLine) =>
  line.orderStatus === 'cancelledByPlatform' || line.orderStatus === 'rejected';

/**
 * What the unmatched lines are worth, and the button that clears them.
 *
 * The money is the point. A line nobody has matched is not a tidiness problem — the food went out,
 * the platform is going to charge commission on it, and it is missing from the check, so the day's
 * takings are short by exactly this figure and no report anywhere says why. Totalled per platform
 * and per currency, because those are separate debts and one added number would be a figure that
 * exists nowhere.
 */
function WaitingToRingUp({ lines }: { lines: PlatformLine[] }) {
  const queryClient = useQueryClient();
  const channels = useEntityIndex('channel');
  const [running, setRunning] = useState<string | null>(null);

  const totals = new Map<string, { channelId: string; currency: string; minor: number; lines: number }>();
  for (const line of lines) {
    if (line.status !== 'needsMatching' || nothingToRecover(line)) continue;
    const channelId = line.channelId ?? '';
    const currency = line.currency ?? 'HNL';
    const key = `${channelId}|${currency}`;
    const found = totals.get(key) ?? { channelId, currency, minor: 0, lines: 0 };
    found.minor += typeof line.lineTotalMinor === 'number' ? line.lineTotalMinor : 0;
    found.lines += 1;
    totals.set(key, found);
  }
  const waiting = [...totals.values()].sort((a, b) => b.minor - a.minor);
  if (waiting.length === 0) return null;

  const rematch = async (channelId: string) => {
    setRunning(channelId);
    try {
      const result = await rawApi<{ matched: number; remaining: number }>('POST', REMATCH_PATH, {
        channelId,
      });
      // Both halves of the answer, always. "4 put on their checks" over a queue that still has 30 in
      // it is how somebody closes the tab believing they finished.
      notifyDone(
        result.matched > 0 ? 'Put on their checks' : 'Nothing could be matched yet',
        result.matched > 0
          ? `${result.matched} ${result.matched === 1 ? 'line' : 'lines'} now ring up. ${
              result.remaining === 0
                ? 'Nothing is left waiting.'
                : `${result.remaining} still waiting for a name to be matched.`
            }`
          : 'Every line still waiting is sold under a name that has no size against it yet. Match the name above first.',
      );
      // The lines, the orders that contain them and the matching queue all change together; leaving
      // any of them stale shows the owner a table that still says the work is outstanding.
      for (const path of [LINES_PATH, PLATFORM_ORDERS_PATH, MAPPINGS_PATH]) {
        void queryClient.invalidateQueries({ queryKey: listKey(path) });
      }
    } catch (err) {
      notifyError(err, 'put these lines on their checks');
    } finally {
      setRunning(null);
    }
  };

  return (
    <Group gap="sm" mb="md" align="stretch">
      {waiting.map((total) => (
        <Paper key={`${total.channelId}|${total.currency}`} withBorder p="sm" radius="md" miw={260}>
          <Text size="xs" c="dimmed">
            {channels.label(total.channelId)}
          </Text>
          <Text size="xl" fw={600} c="orange">
            {formatMoney(total.minor, total.currency)}
          </Text>
          <Text size="sm" c="dimmed" mb="sm">
            sold but not on a check — {total.lines} {total.lines === 1 ? 'line' : 'lines'}
          </Text>
          <Button
            size="xs"
            variant="light"
            loading={running === total.channelId}
            onClick={() => void rematch(total.channelId)}
          >
            Put them on the checks
          </Button>
        </Paper>
      ))}
    </Group>
  );
}

/**
 * The names a platform has actually sold under, straight off their orders.
 *
 * The matching table above lists names; this lists sales. It is the difference between "there is an
 * unmatched item called Fish Tacos" and "Fish Tacos has sold eleven times this week and none of it
 * reached a check" — the second is what makes anyone stop and do the matching.
 */
export function LinesFromOrders() {
  const query = useQuery({
    queryKey: listKey(LINES_PATH),
    queryFn: () => rawApi<PlatformLine[]>('GET', LINES_PATH),
  });

  return (
    <>
      <WaitingToRingUp lines={query.data ?? []} />
      <CrudSection
        title="What they have actually sold"
        description="Every line of every platform order, exactly as they named it. Anything still waiting is at the top: the sale is recorded and the platform owes for it, but it is not on the check until its name is matched above."
        listPath={LINES_PATH}
        emptyMessage="No platform orders have arrived yet, so nothing has been sold under a name to match."
        /*
         * Only on the lines that are actually waiting. A row that already rang up has nothing to
         * match, and one on an order the guest took back has nothing to recover — a button on every
         * row that does nothing on most of them is a button nobody presses on the row that needs it.
         */
        rowActions={(row) =>
          row.status === 'needsMatching' && !nothingToRecover(row as PlatformLine) ? (
            <MatchLineButton line={row as PlatformLine} />
          ) : null
        }
        columns={[
          { accessor: 'receivedAt', title: 'Sold', unit: 'datetime', noWrap: true },
          { accessor: 'channelId', title: 'Platform', reference: 'channel' },
          { accessor: 'platformName', title: 'Their name for it' },
          { accessor: 'qty', title: 'How many' },
          { accessor: 'lineTotalMinor', title: 'Charged for it', unit: 'money' },
          {
            // Not the line's own status alone: an unmatched line on an order the guest took back
            // would otherwise read "Needs matching" in orange for ever, and every attempt to clear
            // it does nothing — the server will not put a line on a cancelled order, correctly. It
            // is the one row on this list that is finished, and it has to say so.
            accessor: 'status',
            title: 'On the check?',
            render: (row) =>
              nothingToRecover(row as PlatformLine) ? (
                <Text size="sm" c="dimmed">
                  Nothing owed — order cancelled
                </Text>
              ) : (
                <Badge color={enumColor('platformLineStatus', row.status)} variant="light">
                  {enumLabel('platformLineStatus', row.status)}
                </Badge>
              ),
          },
        ]}
      />
    </>
  );
}
